<template>
  <div
    class="v-editable-text__wrapper"
    :class="{
      [extraClass]: !!extraClass,
      ['no-click-action']: !activateOnClick
    }"
  >
    <div
      class="icon"
      :class="{
        ['active']: isEditorActive,
        ['not-editable']: notEditable,
        ['has-error']: isFieldHasError,
        ['error-icon']: uniteErrors.length && isInputTouched && !isEditorActive
      }"
      v-if="!hideIcon"
    >
      <img :src="getIcon" />
    </div>
    <div
      class="title"
      v-if="!hideLabel"
      :class="{ ['has-error']: isFieldHasError }"
    >
      {{ label }}
    </div>
    <div
      class="value"
      :class="{
        ['active']: isEditorActive,
        ['not-editable']: notEditable,
        ['has-error']: isFieldHasError
      }"
      v-on="activateOnClick ? { click: activateEditor } : {}"
    >
      <input
        v-show="isEditorActive"
        type="text"
        ref="input"
        :value="value"
        @keyup.enter="onBlur"
        @blur="onBlur"
        @focus="isInputTouched = true"
        @change="onChange"
        @click="e => e.stopPropagation()"
      />
      <div class="value-placeholder" v-show="!isEditorActive && value">
        <span :title="value">{{ value }}{{ placeholderSign }}</span>
      </div>
      <div v-show="!isEditorActive && !value" class="no-val">
        {{ addPlaceholder }}
      </div>
    </div>
    <div class="errors-wrapper">
      <div
        v-if="uniteErrors.length && isInputTouched && !isEditorActive"
        v-for="(error, idx) in uniteErrors"
        :key="idx"
        class="error-messages"
      >
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

//assets
import checkIcon from "@/assets/images/checkmark.svg";
import checkIconActive from "@/assets/images/checkmark-checked.svg";
import checkIconError from "@/assets/images/checkmark-error.svg";

import withToasted from "@/mixins/withToasted";
import withFormUtils from "@/mixins/withFormUtils";

export default Vue.component("VEditableText", {
  props: {
    _key: VueTypes.string.isRequired,
    label: VueTypes.string,
    value: VueTypes.any,
    hideIcon: VueTypes.bool.def(false),
    hideLabel: VueTypes.bool.def(false),
    notEditable: VueTypes.bool.def(false),
    addPlaceholder: VueTypes.string.def("Add"),
    extraClass: VueTypes.string.def(""),
    placeholderSign: VueTypes.string.def(""),
    handleChange: VueTypes.func,
    onBlurHandler: VueTypes.func,
    errors: VueTypes.arrayOf(VueTypes.string).def([]),

    // determine if should input appears on click
    activateOnClick: VueTypes.bool.def(true),

    // works only if activateOnClick is false
    // using for handling editable field outside
    inputActive: VueTypes.bool.def(false),

    //validation callback from parent
    validation: VueTypes.array
  },

  mixins: [withToasted],

  data() {
    return {
      name: this.formName,
      isEditorActive: false,
      currentValue: this.value,
      checkIcon,
      checkIconError,
      checkIconActive,
      prevValue: this.value,
      wasActive: this.inputActive,
      //from direct validators without forms
      additionalErrors: [],
      isInputTouched: false
    };
  },

  computed: {
    //   getContainerClass() {
    //     return {
    //       ["input-container"]: true,
    //       [this.layout]: true,
    //       [this.classType]: true,
    //       [this.extraClass]: true,
    //       ["has-error"]: this.errors.length
    //     };
    //   }

    getIcon() {
      if (
        (this.errors.length || this.additionalErrors.length) &&
        !this.isEditorActive &&
        this.isInputTouched
      ) {
        return this.checkIconError;
      }
      if (this.value && !this.errors.length && !this.additionalErrors.length) {
        return this.checkIconActive;
      }
      return this.checkIcon;
    },

    _isFieldTouched() {
      return this.isFieldTouched(this._key);
    },

    isFieldHasError() {
      return (
        (this.errors.length || this.additionalErrors.length) &&
        this.isInputTouched &&
        !this.isEditorActive
      );
    },

    uniteErrors() {
      return [...this.errors, ...this.additionalErrors];
    }
  },

  methods: {
    activateEditor(e) {
      if (this.notEditable) {
        return;
      }

      if (e) {
        e.stopPropagation();
      }

      this.isEditorActive = true;

      // Focus doesn't triggered for the same tick
      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },

    onBlur(e) {
      e.stopPropagation();
      this.validate(this.currentValue);
      if (this.isEditorActive && this.currentValue !== this.prevValue) {
        this.$emit("change", this._key, this.currentValue);
        this.handleChange && this.handleChange(this._key, this.currentValue);
        this.prevValue = this.currentValue;
      } else {
        this.onBlurHandler && this.onBlurHandler(this._key, this.currentValue);
      }

      this.isEditorActive = false;
      this.wasActive = false;
    },

    onChange(e) {
      const { value } = e.target;
      this.currentValue = value.trim();
    },

    validate(val) {
      if (this.validation.length) {
        let errors = [];
        this.validation.forEach(validator => {
          let error = validator(val);
          if (error) {
            errors.push(error);
          }
        });
        return errors.length
          ? (this.additionalErrors = errors)
          : (this.additionalErrors = []);
      }
    }
  },

  created() {
    document.addEventListener("click", this.onBlur);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.onBlur);
  },

  watch: {
    inputActive: function (val) {
      if (val && val !== this.wasActive) {
        this.activateEditor();
      }

      this.wasActive = val;
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-editable-text";

.#{$ns} {
  &__wrapper {
    display: grid;
    grid-template-rows: auto auto;
    grid-auto-flow: column;
    grid-template-columns: max-content max-content 1fr;
    align-items: center;
    margin-bottom: 10px;
    width: 100%;
    grid-template-areas:
      "icon title field"
      ". . errors";

    .icon {
      margin-right: 8px;
      grid-area: icon;
    }

    .title {
      grid-area: title;
      color: $default-color;
      white-space: nowrap;
      margin-right: 5px;
      width: 72px;
      min-width: 72px;
      /**/
      /*@media screen and (min-width: 991px) {*/
      /*margin-right: 5px;*/
      /*width: auto;*/
      /*min-width: auto;*/
      /*}*/

      &.has-error {
        color: $danger-color;
      }
    }

    .value {
      grid-area: field;
      @include textCropEllipsis();
      grid-area: field;
      border: 1px solid transparent;
      cursor: pointer;
      border-radius: 2px;
      transition: width 0.2s linear;
      width: 100%;

      &.has-error {
        border-color: $danger-color;
      }

      .no-val {
        color: $default-color;
      }

      &:hover,
      &.active {
        border-color: #b6b6ff;
        box-shadow: 0 0 4px rgba(87, 87, 245, 0.14);
      }

      &.not-editable {
        box-shadow: none;
        border-color: transparent;
        cursor: default;
      }

      &.active {
        width: 100%;
        cursor: default;
      }

      div,
      input {
        padding: 3px;
        color: $secondary-text-color;
        font-size: 15px;
        width: 100%;
        font-family: "Lato-Regular", Arial, sans-serif;
        height: $input-height;
        display: flex;
        align-items: center;

        &:hover {
          border-color: #b6b6ff;
          box-shadow: 0 0 4px rgba(87, 87, 245, 0.14);
        }
      }

      div {
        /*padding-bottom: 1px;*/
      }

      input {
        outline: none;
        border: none;
        font-size: 15px;
      }

      .value-placeholder {
        span {
          @include textCropEllipsis();
          line-height: 1.2;
          white-space: pre;
        }
      }
    }

    .errors-wrapper {
      grid-area: errors;
    }

    .error-messages {
      color: $danger-color;
      font-size: 12px;
      line-height: 1.2;
      padding-top: 3px;
    }

    &.no-click-action {
      .value:not(.active):hover {
        border: 1px solid transparent;
        box-shadow: none;
      }

      div.value-placeholder {
        box-shadow: none;
        border: none;
      }
    }
  }
}
</style>
